// 📄 useFacturas.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveInvoiceConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, InvoiceFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const INVOICE_STEP_IDS = ['invoice_params', 'invoice_tax', 'invoice_posting', 'invoice_exits'];

const STEP_KEY_MAP: Record<string, string> = {
  invoice_params: 'step_params', invoice_tax: 'step_tax', invoice_posting: 'step_posting', invoice_exits: 'step_exits'
};

const DEFAULT_FORM: InvoiceFormData = {
  invoiceEnabled: false, invoiceParkNoAcc: false, invHdrPrkNosesPogrxEnb: false,
  companyCodeEdi: '', taxMappingNotes: '',
  glDrPostingKey: '40', glCrPostingKey: '50', vendDrPostingKey: '21', vendCrPostingKey: '31', invoiceDocType: 'RE',
  exitMRMH0002Active: false, swe2ObjectType: 'IDPKINVOIC', swe2Event: 'MASSINPUTFINISHED', swe2ReceiverFunction: 'ARBCIG_INB_IDOC_SUCCESS_EVENT',
  requiresOT: false, otNumber: '', comments: '',
};

export function useFacturas() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('invoice_params');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<InvoiceFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof InvoiceFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && INVOICE_STEP_IDS.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<InvoiceFormData> = {};
    const invoice = devData.channels?.invoice;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.invoice : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (invoice) {
      if (invoice.step_params) {
        if (activeStep === 'invoice_params') {
          const requiresOT = invoice.step_params.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? invoice.step_params : (envChannelData?.step_params || {});
          initialFormData.invoiceEnabled = source.invoiceEnabled || false;
          initialFormData.invoiceParkNoAcc = source.invoiceParkNoAcc || false;
          initialFormData.invHdrPrkNosesPogrxEnb = source.invHdrPrkNosesPogrxEnb || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, invoice_params: invoice.step_params.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, invoice_params: invoice.step_params.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, invoice_params: invoice.step_params.customDevelopment || emptyCustomDevelopment() }));
        qasMap.invoice_params = invoice.step_params.transport?.QAS || emptyTransportConfirmation();
        prdMap.invoice_params = invoice.step_params.transport?.PRD || emptyTransportConfirmation();
      }
      if (invoice.step_tax) {
        if (activeStep === 'invoice_tax') {
          const requiresOT = invoice.step_tax.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? invoice.step_tax : (envChannelData?.step_tax || {});
          initialFormData.companyCodeEdi = source.companyCodeEdi || '';
          initialFormData.taxMappingNotes = source.taxMappingNotes || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, invoice_tax: invoice.step_tax.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, invoice_tax: invoice.step_tax.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, invoice_tax: invoice.step_tax.customDevelopment || emptyCustomDevelopment() }));
        qasMap.invoice_tax = invoice.step_tax.transport?.QAS || emptyTransportConfirmation();
        prdMap.invoice_tax = invoice.step_tax.transport?.PRD || emptyTransportConfirmation();
      }
      if (invoice.step_posting) {
        if (activeStep === 'invoice_posting') {
          const requiresOT = invoice.step_posting.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? invoice.step_posting : (envChannelData?.step_posting || {});
          initialFormData.glDrPostingKey = source.glDrPostingKey || '40';
          initialFormData.glCrPostingKey = source.glCrPostingKey || '50';
          initialFormData.vendDrPostingKey = source.vendDrPostingKey || '21';
          initialFormData.vendCrPostingKey = source.vendCrPostingKey || '31';
          initialFormData.invoiceDocType = source.invoiceDocType || 'RE';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, invoice_posting: invoice.step_posting.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, invoice_posting: invoice.step_posting.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, invoice_posting: invoice.step_posting.customDevelopment || emptyCustomDevelopment() }));
        qasMap.invoice_posting = invoice.step_posting.transport?.QAS || emptyTransportConfirmation();
        prdMap.invoice_posting = invoice.step_posting.transport?.PRD || emptyTransportConfirmation();
      }
      if (invoice.step_exits) {
        if (activeStep === 'invoice_exits') {
          const requiresOT = invoice.step_exits.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? invoice.step_exits : (envChannelData?.step_exits || {});
          initialFormData.exitMRMH0002Active = source.exitMRMH0002Active || false;
          initialFormData.swe2ObjectType = source.swe2ObjectType || 'IDPKINVOIC';
          initialFormData.swe2Event = source.swe2Event || 'MASSINPUTFINISHED';
          initialFormData.swe2ReceiverFunction = source.swe2ReceiverFunction || 'ARBCIG_INB_IDOC_SUCCESS_EVENT';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, invoice_exits: invoice.step_exits.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, invoice_exits: invoice.step_exits.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, invoice_exits: invoice.step_exits.customDevelopment || emptyCustomDevelopment() }));
        qasMap.invoice_exits = invoice.step_exits.transport?.QAS || emptyTransportConfirmation();
        prdMap.invoice_exits = invoice.step_exits.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.invoice;
    const prdChannelData = configSource?.environments?.PRD?.channels?.invoice;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    INVOICE_STEP_IDS.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de Facturas:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    if (activeStep === 'invoice_tax' && !formData.companyCodeEdi.trim()) {
      setValidationError("El Company Code del Partner EDI es obligatorio."); return;
    }
    if (activeStep === 'invoice_posting' && !formData.invoiceDocType.trim()) {
      setValidationError("El Document Type es obligatorio."); return;
    }
    if (activeStep === 'invoice_exits' && !formData.swe2ObjectType.trim()) {
      setValidationError("El Object Type de SWE2 es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveInvoiceConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'invoice', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'invoice', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange,
  };
}
